import CardGiftcardOutlinedIcon from '@mui/icons-material/CardGiftcardOutlined';
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined';
import TaskOutlinedIcon from '@mui/icons-material/TaskOutlined';
import { Box, Button, Stack, SvgIconTypeMap, Typography } from '@mui/material';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import { BenefitDTOBenefitEnum } from 'probonio-shared-ui/api';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import BenefitHint from '../../component/benefitTabs/BenefitHint';
import { BenefitPaper } from '../../component/benefitTabs/BenefitPaper';
import { getBenefitIcon } from '../../module/benefits/BenefitIcon';
import BenefitSubPage from '../../module/benefits/BenefitSubPage';
import { ITLeasingLinks } from '../../module/benefits/itLeasing/links';
import { AuthorizedLayout } from '../../module/navigation/AuthorizedLayout';

export const ITLeasingLearnMorePage: React.FC = () => {
  const { t } = useTranslation('benefitsModule');
  return (
    <AuthorizedLayout
      title={t('IT_LEASING.settings.title')}
      breadcrumbs={[{ title: t('IT_LEASING.title'), to: '/benefits/it_leasing/settings' }]}
    >
      <BenefitSubPage
        icon={getBenefitIcon(BenefitDTOBenefitEnum.ItLeasing)}
        title={t('IT_LEASING.title')}
        subTitle={t('IT_LEASING.settings.title')}
      >
        <BenefitPaper sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
          <Stack alignSelf="center" spacing={1} width="60%" pt={2}>
            <Typography variant="h1">
              <Trans i18nKey="benefitsModule:IT_LEASING.learnMore.about.title" />
            </Typography>
            <Typography color="text.secondary" variant="body2" pb={2}>
              <Trans i18nKey="benefitsModule:IT_LEASING.learnMore.about.description" />
            </Typography>

            <Typography variant="h1">
              <Trans i18nKey="benefitsModule:IT_LEASING.learnMore.howItWorks.title" />
            </Typography>
            <Typography color="text.secondary" variant="body2" pb={2}>
              <Trans i18nKey="benefitsModule:IT_LEASING.learnMore.howItWorks.description" />
            </Typography>

            <Box pb={2}>
              <Typography variant="h1" pb={1}>
                <Trans i18nKey="benefitsModule:IT_LEASING.learnMore.advantages.title" />
              </Typography>

              <Box display="flex" alignItems="flex-start" pt={1}>
                <StyledIcon icon={CardGiftcardOutlinedIcon} />
                <Box>
                  <Typography variant="h2">
                    <Trans i18nKey="benefitsModule:IT_LEASING.learnMore.advantages.first.title" />
                  </Typography>

                  <Typography color="text.secondary" variant="body2" pb={1} pt={1}>
                    <Trans i18nKey="benefitsModule:IT_LEASING.learnMore.advantages.first.description" />
                  </Typography>
                </Box>
              </Box>

              <Box display="flex" alignItems="flex-start" pt={1}>
                <StyledIcon icon={SavingsOutlinedIcon} />
                <Box>
                  <Typography variant="h2">{t('IT_LEASING.learnMore.advantages.second.title')}</Typography>
                  <Typography color="text.secondary" variant="body2" pb={1} pt={1}>
                    <Trans i18nKey="benefitsModule:IT_LEASING.learnMore.advantages.second.description" />
                  </Typography>
                </Box>
              </Box>

              <Box display="flex" alignItems="flex-start" pt={1}>
                <StyledIcon icon={TaskOutlinedIcon} />
                <Box>
                  <Typography variant="h2">{t('IT_LEASING.learnMore.advantages.third.title')}</Typography>
                  <Typography color="text.secondary" variant="body2" pb={1} pt={1}>
                    <Trans i18nKey="benefitsModule:IT_LEASING.learnMore.advantages.third.description" />
                  </Typography>
                </Box>
              </Box>
            </Box>

            <BenefitHint
              title={t('IT_LEASING.hint.customer.title')}
              description={
                <Stack spacing={1}>
                  <Typography color="text.secondary" variant="body2">
                    {t('IT_LEASING.hint.customer.description')}
                  </Typography>
                  <Button
                    variant="contained"
                    size="small"
                    href={ITLeasingLinks.LOGIN}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ width: 'fit-content' }}
                  >
                    {t('IT_LEASING.hint.customer.button')}
                  </Button>
                </Stack>
              }
            />
            <BenefitHint
              title={t('IT_LEASING.hint.faq.title')}
              description={
                <Typography color="text.secondary" variant="body2">
                  <Trans
                    i18nKey="benefitsModule:IT_LEASING.hint.faq.description"
                    components={{
                      email: <a href={ITLeasingLinks.HR_SUPPORT_MAIL_TO} />,
                    }}
                  />
                </Typography>
              }
            />
          </Stack>
        </BenefitPaper>
      </BenefitSubPage>
    </AuthorizedLayout>
  );
};

type StyledIconProps = {
  icon: OverridableComponent<SvgIconTypeMap<{}, 'svg'>> & {
    muiName: string;
  };
};

const StyledIcon: React.FC<StyledIconProps> = ({ icon: Icon }) => {
  return <Icon sx={{ fontSize: 45, mr: 2, ml: 1, mt: 0.2 }} />;
};

export default ITLeasingLearnMorePage;
